// src/api/cartApi.ts
import axios from "axios";
import { tokenStore } from "../services/token";

const RAW_API_URL = process.env.REACT_APP_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");

// Guest id key
const KEY = "od_guest_id";

export function getGuestId(): string {
  if (typeof window === "undefined") return "guest_ssr";

  const stored = localStorage.getItem(KEY);
  if (stored) return stored;

  const newId: string =
    (globalThis.crypto as any)?.randomUUID?.() ??
    `g_${Math.random().toString(16).slice(2)}_${Date.now()}`;

  localStorage.setItem(KEY, newId);
  return newId;
}

export function setGuestId(id: string) {
  if (typeof window === "undefined") return;
  if (!id) return;
  localStorage.setItem(KEY, String(id));
}

export function clearGuestId() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(KEY);
}

export const cartHttp = axios.create({
  baseURL: API_URL,
});

// ✅ gắn Bearer token nếu có (để sync cart hoặc các api authorize chạy được)
cartHttp.interceptors.request.use((config) => {
  const token = tokenStore.getAccess?.() ?? null;
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

export type CartLine = {
  cartId: number;
  productId: number;
  productName: string;
  imageUrl?: string | null;
  unitPrice: number;
  quantity: number;
  color?: string | null;
  size?: string | null;
};

function toNum(x: any, fallback = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeCartLine(x: any): CartLine {
  const basePrice = toNum(x?.basePrice ?? x?.base_price ?? 0);
  const finalPrice = toNum(x?.finalPrice ?? x?.final_price ?? 0);

  return {
    cartId: toNum(x?.cartId ?? x?.id ?? x?.cartItemId ?? 0),
    productId: toNum(x?.productId ?? x?.idProduct ?? 0),
    productName: String(x?.productName ?? x?.name ?? x?.product?.name ?? ""),
    imageUrl:
      x?.imageUrl ??
      x?.thumbnail ??
      x?.productImage ??
      x?.product?.thumbnail ??
      x?.product?.imageUrl ??
      null,

    // ✅ FIX: ưu tiên unitPrice nếu có, nếu không thì finalPrice, cuối cùng fallback basePrice
    unitPrice: toNum(
      x?.unitPrice ??
        x?.price ??
        (finalPrice > 0 ? finalPrice : basePrice) ??
        x?.salePrice ??
        0
    ),

    quantity: toNum(x?.quantity ?? x?.qty ?? 0),
    color: x?.color ?? null,
    size: x?.size ?? null,
  };
}


/* ======================
   GET LIST
   GET /api/Cart/GetList?guestId=...
====================== */
export async function cartGetList(): Promise<CartLine[]> {
  const guestId = getGuestId();
  const res = await cartHttp.get(`/api/Cart/GetList`, { params: { guestId } });
  const raw = res.data;

  const arr: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.data)
    ? raw.data
    : [];

  return arr.map(normalizeCartLine);
}

/* ======================
   COUNT
   GET /api/Cart/cart/count?guestId=...
   response: { count }
====================== */
export async function cartGetCount(): Promise<number> {
  const guestId = getGuestId();
  const res = await cartHttp.get(`/api/Cart/cart/count`, { params: { guestId } });
  return toNum(res.data?.count, 0);
}

/* ======================
   ADD
   POST /api/Cart/Add
   guest: GuestId required
   user: backend lấy userId từ token claim
====================== */
export async function cartAdd(payload: {
  productId: number;
  color: string;
  size: string;
  quantity: number;
}) {
  const guestId = getGuestId();

  const body = {
    productId: payload.productId,
    color: payload.color,
    size: payload.size,
    quantity: payload.quantity,
    guestId,
  };

  const res = await cartHttp.post(`/api/Cart/Add`, body);

  // backend có thể trả guestId (để client lưu)
  const newGuestId = (res.data as any)?.guestId;
  if (newGuestId) setGuestId(String(newGuestId));

  return res.data;
}

/* ======================
   UPDATE
   PUT /api/Cart/Update
   body: { cartId, quantity, guestId }
====================== */
export async function cartUpdate(payload: { cartId: number; quantity: number }) {
  const guestId = getGuestId();
  const body = { cartId: payload.cartId, quantity: payload.quantity, guestId };
  const res = await cartHttp.put(`/api/Cart/Update`, body);
  return res.data;
}

/* ======================
   DELETE
   DELETE /api/Cart/Delete/{cartId}?guestId=...
====================== */
export async function cartDelete(cartId: number) {
  const guestId = getGuestId();
  const res = await cartHttp.delete(`/api/Cart/Delete/${cartId}`, {
    params: { guestId },
  });
  return res.data;
}

/* ======================
   CLEAR
   DELETE /api/Cart/Clear?guestId=...
====================== */
export async function cartClear() {
  const guestId = getGuestId();
  const res = await cartHttp.delete(`/api/Cart/Clear`, { params: { guestId } });
  return res.data;
}

/* ======================
   SYNC (Authorize)
   POST /api/Cart/cart/sync
   body: string guestId
====================== */
export async function cartSyncGuestToUser() {
  const guestId = getGuestId();

  // backend nhận body string guestId
  // axios gửi JSON string => OK
  const res = await cartHttp.post(
    `/api/Cart/cart/sync`,
    JSON.stringify(guestId),
    {
      headers: { "Content-Type": "application/json" },
    }
  );

  return res.data;
}
